import { PostLoginActionNameEnum } from '@sparkware/uc-sdk-core';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
    Level,
} from '@unified-client/event-formatter';
import Container, { Service } from 'typedi';
import { ClickStreamTrackingProvider } from '../tracking';
import { PostLoginEvents } from './enums';

@Service()
export class PostLoginEventEmitter {
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _trackingProvider: ClickStreamTrackingProvider;

    constructor() {
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('PostLoginEventEmitter');
        this._trackingProvider = Container.get(ClickStreamTrackingProvider);
    }

    public sendActionStartEvent = (actionName: PostLoginActionNameEnum, correlationID: string) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendActionStartEvent');
        const elasticEventObj = formatter.formatUCEvent(
            { message: `Start post login action ${actionName}.` },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.ActionStarted, actionName: actionName },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendActionCompleteEvent = (
        actionName: PostLoginActionNameEnum,
        correlationID: string,
    ) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendActionCompleteEvent');
        const elasticEventObj = formatter.formatUCEvent(
            { message: `Complete post login action ${actionName}.` },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.ActionCompleted, actionName },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendFlowStart = (actionsToExecute: string, correlationID: string) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendFlowStart');
        const elasticEventObj = formatter.formatUCEvent(
            {
                message: `Start post login actions flow. Actions to be executed: ${actionsToExecute}`,
            },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.FlowStart },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendFlowResumed = (
        lastActionInExecutionName: PostLoginActionNameEnum,
        actionsToExecute: string,
        correlationID: string,
    ) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendFlowResumed');
        const elasticEventObj = formatter.formatUCEvent(
            {
                message: `Resume post login actions flow from actionName=${lastActionInExecutionName}. Actions to be executed: ${actionsToExecute}`,
            },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.FlowResumed },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendFlowSkipped = (correlationID: string) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendFlowSkipped');
        const elasticEventObj = formatter.formatUCEvent(
            { message: `Post login actions flow was skipped.` },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.FlowSkipped },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendFlowError = (error: Error, correlationID: string) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendFlowError');
        const elasticEventObj = formatter.formatUCEvent(
            {
                message: `Post login actions flow was disrupted by an error: message=${
                    error?.message || '-'
                }, name:${error.name || '-'}, stack= ${error?.stack || '-'}`,
            },
            { correlationID, level: Level.error },
            { event: PostLoginEvents.FlowError },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };

    public sendFlowCompleted = (correlationID: string) => {
        const formatter = this._eventFormatterBuilder.createFormatter('sendFlowCompleted');
        const elasticEventObj = formatter.formatUCEvent(
            { message: `Post login actions flow was completed.` },
            { correlationID, level: Level.information },
            { event: PostLoginEvents.FlowCompleted },
        );

        this._trackingProvider.sendEventV2(elasticEventObj);
    };
}
